import { toUnitValStrWithFormat } from '@/components/FlowSheetEditor/components/RightPanel/UnitPanel/components/common/utils'
import { COLUMNS } from '@/components/FlowSheetEditor/utils'
import { PropertySetModule } from '@/services/admin/property-set/modal'
import { MaterialBlanceCaseStream, MaterialBlanceState, StreamPhase } from '@/services/project/material-balance.service'
import { UnitEntity, UnitSet } from '@/services/unitSet/unitSet.model'
import ExcelJS  from 'exceljs'
import isNumber from "lodash/isNumber";

export interface TableRowItem {
  label: string
  unit?: string
  key?: string
  streams: Array<{
    id: string
    value: any
  }>
}


export interface IMaterialBalanceData {
  composition: TableRowItem[]
  bulkData: TableRowItem[]
  others: Array<{
    name: string
    data: TableRowItem[]
  }>
}

function getUnitEntity(unitSet: UnitSet, dimCode: string | undefined) {
  if (!(unitSet && dimCode)){
    return;
  }
  return unitSet.units.find(item => item.dimcode === dimCode)
}

function getComposition(bulkPhaseMap, streams, pps: PropertySetModule.Modal, unitSet: UnitSet): TableRowItem[] {
  const components = pps.components || []
  const molFracEntiry = getUnitEntity(unitSet, 'MOL_FRAC')
  return components.map((item, componentIndex) => {
    return {
      label: item.name,
      streams: streams.map(stream => {
        let val = bulkPhaseMap[stream.id]?.x[componentIndex]
        if (isNumber(val)) {
        val =  toUnitValStrWithFormat(val as number, molFracEntiry)
      }
        return {
          id: stream.id,
          value: val
        }
      })
    }
  })
}

const BulkAttrs = [
  {label: 'Flow', dim: 'MOL_F', key: 'f'},
  {label: 'Enthalpy', dim: 'MOL_H', key: 'h'},
  {label: 'HHV', key: 'hhv'},
  {label: 'LHV', key: 'lhv'},
  {label: 'Molecular Weight', dim: 'MW', key: 'mw'},
  {label: 'Pressure', dim: 'PRES', key: 'p'},
  {label: 'Critical Pressure', dim: 'PRES', key: 'pc'},
  {label: 'Phase Fraction', dim: 'PF', key: 'pf'},
  {label: 'Cp', key: 'cp'},
  {label: 'Cv', key: 'cv'},
  {label: 'Viscosity', dim: 'VISC', key: 'eta'},
  {label: 'Surface Tension', key: 'k'},
]

function getPhaseData(stream: MaterialBlanceCaseStream, phaseName: string): StreamPhase {
  return stream.phases.find(item => item.phase === phaseName)!
}

function getStreamDataWithUnit(stream: MaterialBlanceCaseStream, phase: string, attribute: string, unitEntiry?: UnitEntity) {
  const phaseData = getPhaseData(stream, phase)
  const val = phaseData && phaseData[attribute];
  if (phaseData) {
    return toUnitValStrWithFormat(val as number, unitEntiry)
  }
  
  return val || '-';
}

function getBulkData(streams: MaterialBlanceCaseStream[], unitSet: UnitSet): TableRowItem[] {
  return BulkAttrs.map(attr => {
    const unitEntiry = getUnitEntity(unitSet, attr.dim)
    return {
      label: attr.label,
      unit: unitEntiry?.unit.code,
      streams: streams.map(stream => {
        return {
          id: stream.id,
          value: getStreamDataWithUnit(stream, 'bulk', attr.key, unitEntiry)
        }
      })
    }
  })
}

const OtherPhaseAttrs = [
  {label: 'State', key: 'state'},
  {label: 'Flow', dim: 'MOL_F', key: 'f'},
  {label: 'Phase Fraction', dim: 'VF', key: 'pf'},
  {label: 'Vapor Fraction', dim: 'VF', key: 'vf'},
  {label: 'Molecular Weight', dim: 'MW', key: 'mw'},
  {label: 'Molar Enthalpy', dim: 'MOL_H', key: 'h'},
  {label: 'Molar Entropy', dim: 'MOL_S', key: 's'},
  {label: 'Molar Cp', key: 'cp'},
  {label: 'Molar Cv', key: 'cv'},
  {label: 'Compressibility Factor', key: 'f'},
  {label: 'Molar Density', dim: 'MOL_RHO', key: 'rho'},
  {label: 'Molar LHV', key: 'lhv'},
  {label: 'Molar HHV', key: 'hhv'},
  {label: 'Dynamic Viscosity', dim: 'VISC', key: 'eta'},
  {label: 'Kinematic Viscosity', key: 'nu'},
  {label: 'Surface Tension', key: 'sigma'},
  {label: 'Conductivity', key: 'k'},
  {label: 'Critical Temperature', dim: 'TEMP', key: 'tc'},
  {label: 'Critical Pressure', dim: 'PRES', key: 'pc'},
  {label: 'Critical Compressibility Factor', key: 'zc'},
]

/*
        dto.setPhase(this.getPhaseName());
//        dto.setState(this.getState());
//        dto.setF(this.getMolarFlow());
//        dto.setT(this.getTemperature());
//        dto.setP(this.getPressure());
//        dto.setPf(this.getMolarPhaseFrac());
//        dto.setVf(this.getMolarVaporFrac());
//        dto.setMw(this.getMolarWeight());
//        dto.setH(this.getMolarEnthalpy());
//        dto.setS(this.getMolarEntropy());
//        dto.setCp(this.getMolarCp());
//        dto.setCv(this.getMolarCv());
//        dto.setZ(this.getZ());
//        dto.setRho(this.getMolarDensity());
//        dto.setLhv(this.getMolarLHV());
//        dto.setHhv(this.getMolarHHV());
//        dto.setEta(this.getViscosity());
//        dto.setNu(this.getKinematicViscosity());
//        dto.setSigma(this.getSurfaceTension());
//        dto.setK(this.getConductivity());
        dto.setPc(this.getCriticalPressure());
        dto.setTc(this.getCriticalTemperature());
        dto.setZc(this.getCriticalZ());

*/
function getOtherPhasesData(streams: MaterialBlanceCaseStream[], unitSet: UnitSet) {
  const firstStream = streams[0]
  const otherPhases = firstStream.phases.map(item => item.phase).filter(item => item !== 'bulk')
  return otherPhases.map((name) => {
    return {
      name,
      data: OtherPhaseAttrs.map(attr => {
        const unitEntiry = getUnitEntity(unitSet, attr.dim)
        return {
          label: attr.label,
          unit: unitEntiry?.unit.code,
          streams: streams.map(stream => {
            return {
              id: stream.id,
              value: getStreamDataWithUnit(stream, name, attr.key, unitEntiry)
            }
          })
        }
      })
    }
  })
}

export function getMaterialBalanceData(mb: MaterialBlanceState, pps: PropertySetModule.Modal, unitSet: UnitSet) {
  const streams = mb.streams;
  const bulkPhaseMap = streams.reduce((res, cur) => {
    const targetPhase = cur.phases.find(item => item.phase === 'bulk')
    res[cur.id] = targetPhase
    return res;
  }, {})
  
  return {
    composition: getComposition(bulkPhaseMap, streams, pps, unitSet),
    bulkData: getBulkData(streams, unitSet),
    others: getOtherPhasesData(streams, unitSet),
  }
}


export async function exportMaterialBalanceFile(data: IMaterialBalanceData, sheetName = 'Sheet 1') {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName);
  sheet.mergeCells('A1:C1')
  sheet.getCell('A1').value = 'Stream No.'
  const firstComposion = data.composition[0]
  firstComposion.streams.forEach((stream, sIndex) => {
    const col = COLUMNS[3 + sIndex]
    sheet.getCell(`${col}${1}`).value = sIndex + 1
    sheet.mergeCells(`${col}2:${col}3`)
    sheet.getCell(`${col}${2}`).value = `Stream ${sIndex + 1}`
  })
  sheet.mergeCells('A2:C2')
  sheet.getCell('A2').value = 'Medium / Description'
  sheet.mergeCells('B3:C3')
  sheet.getCell('B3').value = 'Component'
  // fill the component
  sheet.mergeCells(`A4:A${data.composition.length + 3}`)
  const a4cell = sheet.getCell('A4')
  a4cell.value = 'Mole-%'
  a4cell.alignment = { textRotation: 90, vertical: 'middle', horizontal: 'center' }
  data.composition.map((item, index) => {
    const row = index + 4;
    sheet.getCell(`B${row}`).value = item.label
    sheet.mergeCells(`B${row}:C${row}`)
    item.streams.forEach((stream, sIndex) => {
      const col = COLUMNS[3 + sIndex]
      sheet.getCell(`${col}${row}`).value = stream.value
    })
  })


  // fill the bulk data
  const bulkIndex = 4 + data.composition.length;
  sheet.getCell(`A${bulkIndex}`).value = `Bulk Phase Properties`
  let baseIndex = bulkIndex + 1;
  fillPhaseData(data.bulkData, baseIndex)
  // fill other phases
  baseIndex += data.bulkData.length
  data.others.forEach((phase) => {
    sheet.getCell(`A${baseIndex}`).value = `${bigFirstChar(phase.name)} Phase Properties`
    fillPhaseData(phase.data, baseIndex + 1)
    baseIndex += phase.data.length + 1
  })
  const buffer = await workbook.xlsx.writeBuffer()
  downloadFile(buffer)

  function fillPhaseData(phaseData: TableRowItem[], baseIndex: number) {
    phaseData.forEach((item, attIndex) => {
      const row = baseIndex + attIndex
      sheet.mergeCells(`A${row}:B${row}`)
      sheet.getCell(`A${row}`).value = item.label
      sheet.getCell(`C${row}`).value = item.unit
      item.streams.map((stream, sIndex) => {
        const col = COLUMNS[3 + sIndex]
        sheet.getCell(`${col}${row}`).value = stream.value
      })
    })
  }
}

function downloadFile(buffer: ExcelJS.Buffer) {
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  let link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = 'your_filename.xlsx'; // Replace with your desired filename
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
}

export function bigFirstChar(str: string) {
  return str.replace(/^([a-zA-Z])/, (subStr) => {
    return subStr.toUpperCase()
  })
}